import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyboardOff, ShieldAlert, Accessibility, ClipboardX, Save, Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function AdminAntiPaste() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [events, setEvents] = useState([]);
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, e, st] = await Promise.all([
        api.get("/admin/anti-paste/settings"),
        api.get("/admin/anti-paste/events?limit=200"),
        api.get("/instructor/students"),
      ]);
      setSettings(s.data);
      setOverrides(s.data.lab_overrides || {});
      setEvents(e.data.events);
      setStudents(st.data.students);
    } catch (err) { toast.error(apiErr(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (!settings) return <div className="h-[60vh] grid place-items-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>;

  const effEnforce = (labId) => (labId in overrides ? overrides[labId] : settings.enforce_global);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/anti-paste/settings", {
        enforce_global: settings.enforce_global,
        restrict_copy: settings.restrict_copy,
        lab_overrides: overrides,
      });
      toast.success("Enforcement settings saved");
      setSettings((s) => ({ ...s, lab_overrides: overrides }));
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const toggleAccessibility = async (id, val) => {
    try {
      await api.put(`/admin/students/${id}/accessibility`, { allow_paste: val });
      setStudents((list) => list.map((s) => (s.id === id ? { ...s, allow_paste: val } : s)));
      toast.success(val ? "Paste exception granted" : "Exception removed");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const labsByLevel = {};
  (settings.labs || []).forEach((l) => { (labsByLevel[l.level] = labsByLevel[l.level] || []).push(l); });
  const filtered = students.filter((s) => (s.name + s.email).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="admin-antipaste-page">
      <div className="flex items-center gap-3">
        <KeyboardOff className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Manual Command Entry</h1>
          <p className="text-muted-foreground text-sm mt-1">Enforce hands-on typing by blocking copy & paste in student lab terminals.</p>
        </div>
      </div>

      <Tabs defaultValue="settings" className="mt-6">
        <TabsList>
          <TabsTrigger value="settings" data-testid="tab-ap-settings"><ShieldAlert className="h-4 w-4 mr-1.5" />Enforcement</TabsTrigger>
          <TabsTrigger value="accessibility" data-testid="tab-ap-accessibility"><Accessibility className="h-4 w-4 mr-1.5" />Accessibility</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-ap-events"><ClipboardX className="h-4 w-4 mr-1.5" />Attempts ({events.length})</TabsTrigger>
        </TabsList>

        {/* Enforcement settings */}
        <TabsContent value="settings" className="mt-4 space-y-4">
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <label className="flex items-center justify-between gap-4">
              <div><div className="font-medium">Enforce manual entry globally</div><div className="text-sm text-muted-foreground">Block paste in every lab terminal by default.</div></div>
              <Switch checked={settings.enforce_global} onCheckedChange={(v) => setSettings((s) => ({ ...s, enforce_global: v }))} data-testid="toggle-global" />
            </label>
            <label className="flex items-center justify-between gap-4 border-t border-border pt-4">
              <div><div className="font-medium">Restrict copying from instructions & hints</div><div className="text-sm text-muted-foreground">Disable text selection/copy on the lesson and task panels (only where enforcement is on).</div></div>
              <Switch checked={settings.restrict_copy} onCheckedChange={(v) => setSettings((s) => ({ ...s, restrict_copy: v }))} data-testid="toggle-restrict-copy" />
            </label>
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-medium">Per-lab overrides</div>
              <Button variant="ghost" size="sm" onClick={() => setOverrides({})} data-testid="clear-overrides">Clear all overrides</Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Toggle a specific lab to override the global setting. Untouched labs follow the global default.</p>
            <div className="space-y-4">
              {Object.keys(labsByLevel).sort((a, b) => a - b).map((lvl) => (
                <div key={lvl}>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Level {lvl}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {labsByLevel[lvl].map((l) => (
                      <label key={l.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2" data-testid={`lab-override-${l.id}`}>
                        <span className="text-sm truncate">{l.title}{l.id in overrides && <Badge variant="secondary" className="ml-2 text-[10px]">override</Badge>}</span>
                        <Switch checked={effEnforce(l.id)} onCheckedChange={(v) => setOverrides((o) => ({ ...o, [l.id]: v }))} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button className="gap-2" onClick={save} disabled={saving} data-testid="save-antipaste">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
          </Button>
        </TabsContent>

        {/* Accessibility exceptions */}
        <TabsContent value="accessibility" className="mt-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground mb-4">Grant individual students a paste exception for accessibility needs. Exempted students bypass all enforcement.</p>
            <Input placeholder="Search students…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3 max-w-sm" data-testid="ap-student-search" />
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/50 text-left"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3 text-right">Paste exception</th></tr></thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">No students found.</td></tr>}
                  {filtered.map((s) => (
                    <tr key={s.id} className="border-t border-border" data-testid={`ap-student-${s.id}`}>
                      <td className="p-3 font-medium">{s.name}</td>
                      <td className="p-3 text-muted-foreground">{s.email}</td>
                      <td className="p-3 text-right"><Switch checked={!!s.allow_paste} onCheckedChange={(v) => toggleAccessibility(s.id, v)} data-testid={`ap-exception-${s.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Paste attempt log */}
        <TabsContent value="events" className="mt-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground">Recent blocked paste attempts. Attempts are logged, never executed — students are not penalized.</p>
              <Button variant="outline" size="sm" onClick={load} data-testid="refresh-events">Refresh</Button>
            </div>
            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/50 text-left"><th className="p-3">Student</th><th className="p-3">Lab</th><th className="p-3">Method</th><th className="p-3">When</th></tr></thead>
                <tbody>
                  {events.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No paste attempts logged yet.</td></tr>}
                  {events.map((e) => (
                    <tr key={e._id} className="border-t border-border" data-testid={`paste-event-${e._id}`}>
                      <td className="p-3"><div className="font-medium">{e.user_name}</div><div className="text-xs text-muted-foreground">{e.email}</div></td>
                      <td className="p-3 font-mono text-xs">{e.lab_id}</td>
                      <td className="p-3"><Badge variant="outline" className="text-[10px]">{e.method}</Badge></td>
                      <td className="p-3 text-muted-foreground text-xs">{fmt(e.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
